import React, { useState, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { services, stats, testimonials, contactInfo } from '../../data/dreamlandData';
import {
  Waves, Wind, Wifi, UtensilsCrossed, ConciergeBell, Car, Baby, PawPrint, ChefHat,
  Star, Quote, ChevronLeft, ChevronRight
} from 'lucide-react';

const iconMap = {
  Waves, Wind, Wifi, UtensilsCrossed, ConciergeBell, Car, Baby, PawPrint, ChefHat
};


const SectionTitle = ({ title, subtitle }) => (
  <div className="text-center mb-12">
    <h2 className="text-3xl md:text-4xl lg:text-5xl font-['Playfair_Display'] font-bold text-[#1B3D6E] mb-2">
      {title}
    </h2>
    {subtitle && (
      <p className="text-[#F5C518] font-['Dancing_Script'] text-xl mt-1">{subtitle}</p>
    )}
    <div className="flex items-center justify-center gap-3 mt-5">
      <span className="w-12 h-[1px] bg-[#F5C518]" />
      <span className="w-2 h-2 bg-[#F5C518] rotate-45" />
      <span className="w-12 h-[1px] bg-[#F5C518]" />
    </div>
  </div>
);

// ============= SERVICES SECTION =============
const ServiceCard = ({ service, index, inView, Icon }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="bg-white rounded-xl p-8 text-center border border-gray-100 transition-all duration-500 cursor-default"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(30px)",
        transition: `all 0.6s ease-out ${index * 0.1}s`,
        borderColor: hovered ? "rgba(36,120,200,0.30)" : "",
        boxShadow: hovered ? "0 8px 40px rgba(27,61,110,0.15)" : "",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Icon circle — gold fill on hover */}
      <div
        className="w-16 h-16 mx-auto mb-5 rounded-full flex items-center justify-center transition-colors duration-500"
        style={{ background: hovered ? "#F5C518" : "#EEF6FF" }}
      >
        <Icon
          size={28}
          style={{
            color: hovered ? "#1B3D6E" : "#2478C8",
            transition: "color 0.5s",
          }}
        />
      </div>

      <h3 className="text-xl font-['Playfair_Display'] font-semibold text-[#1B3D6E] mb-3">
        {service.title}
      </h3>

      <p className="text-[#6B6B6B] text-sm leading-relaxed">
        {service.description}
      </p>
    </div>
  );
};

// Mobile-only slider card
// Mobile-only slider card — FIXED
const MobileServiceCard = ({ service, Icon }) => {
  return (
   <div className="snap-start shrink-0 w-[calc(90vw-2rem)] rounded-2xl overflow-hidden bg-white shadow-md border border-gray-100">
      {/* Icon area — light blue background */}
      <div className="w-full h-48  bg-white flex items-center justify-center">
        <div className="w-20 h-20 rounded-full bg-[#EEF6FF] flex items-center justify-center shadow-sm">
          <Icon size={36} style={{ color: "#2478C8" }} />
        </div>
      </div>

      {/* Text area — white background */}
      <div className="px-5 py-4 bg-[#EEF6FF]">
        <p className="font-bold text-[#1B3D6E] text-sm uppercase tracking-wide leading-tight mb-2"
           style={{ fontFamily: "'Playfair Display', serif" }}>
          {service.title}
          
        </p>
        <p className="text-[#6B6B6B] text-sm leading-relaxed line-clamp-3">
          {service.description}
        </p>
      </div>
    </div>
  );
};


export const ServicesSection = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const scrollRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const handleScroll = () => {
    if (!scrollRef.current) return;
const cardWidth = scrollRef.current.offsetWidth - 32 + 16;
    const scrollLeft = scrollRef.current.scrollLeft;
    setActiveIndex(Math.round(scrollLeft / cardWidth));
  };

  const scrollToIndex = (i) => {
    if (!scrollRef.current) return;
const cardWidth = scrollRef.current.offsetWidth - 32 + 16;
    scrollRef.current.scrollTo({ left: i * cardWidth, behavior: "smooth" });
    setActiveIndex(i);
  };

  return (
    <section
      id="services"
      className="relative py-20 lg:py-28 bg-[#EEF6FF] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-8 relative z-10">
        <SectionTitle title="What We Offer" />

        {/* ── MOBILE SLIDER (visible only on small screens) ── */}
        <div className="block md:hidden mt-8">
          {/* Swipe hint */}
          <div className="flex items-center gap-2 mb-4 text-[#2478C8]">
            <span className="text-base">→</span>
            <span className="uppercase tracking-widest text-xs font-semibold">
              Swipe to browse
            </span>
          </div>

          {/* Scrollable cards */}
         {/* Scrollable cards */}
<div
  ref={scrollRef}
  onScroll={handleScroll}
  className="flex gap-8 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4 ml-[20px]"
  style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
>
  {services.map((service) => {
    const Icon = iconMap[service.icon];
    return (
      <MobileServiceCard
        key={service.id}
        service={service}
        Icon={Icon}
      />
    );
  })}
</div>

          {/* Dot indicators */}
          <div className="flex justify-center items-center gap-2 mt-5">
            {services.map((_, i) => (
              <button
                key={i}
                onClick={() => scrollToIndex(i)}
                className="rounded-full transition-all duration-300"
                style={{
                  width: i === activeIndex ? "24px" : "10px",
                  height: "10px",
                  background: i === activeIndex ? "#E07B39" : "#CBD5E1",
                }}
              />
            ))}
          </div>
        </div>

        {/* ── DESKTOP GRID (hidden on small screens) ── */}
        <div
          ref={ref}
          className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8"
        >
          {services.map((service, index) => {
            const Icon = iconMap[service.icon];
            return (
              <ServiceCard
                key={service.id}
                service={service}
                index={index}
                inView={inView}
                Icon={Icon}
              />
            );
          })}
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none">
        <svg
          className="relative block w-[calc(100%+1.3px)] h-[50px] sm:h-[70px] md:h-[90px]"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
        >
          <path
            d="M0,0V46.29c47.29,22,104.26,29.05,158,17C230.6,47,284,4,339,1.79c54.69-2.21,108.62,38.69,163,47.71C580,61,634,33,688,20.29c54.41-12.86,108.61-3,163,7.71C905,38,959,51,1013,46.29c54-4.71,108-28.71,162-37.71V0Z"
            opacity="0.25"
            fill="#2478C8"
          />
          <path
            d="M0,0V15.81C47.42,35,103.89,52,158,50.29c72.41-2.21,136.34-49,206-48.5,70.08.5,136.64,48,206,55.21,69.58,7.29,136-24,206-34.71,69.92-10.86,136.61-1,206,10.71C1045,43,1100,60,1200,60V0Z"
            fill="#1B3D6E"
          />
        </svg>
      </div>
    </section>
  );
};


// ============= STATS SECTION =============
const AnimatedCounter = ({ target, suffix }) => {
  const [count, setCount] = useState(0);
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.5 });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const end = target;
    const duration = 2000;
    const increment = end / (duration / 16);

    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [inView, target]);

  return (
    <span ref={ref} className="text-5xl md:text-6xl font-bold font-['Playfair_Display'] text-white">
      {count}{suffix}
    </span>
  );
};

export const StatsSection = () => (
  <section className="relative py-20 overflow-hidden">
    <div
      className="absolute inset-0 bg-cover bg-center bg-fixed"
      style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1516203722367-8b43ba2ec073?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHx0cm9waWNhbCUyMGJlYWNoJTIwcmVzb3J0fGVufDB8fHx8MTc3NjE0NzEyN3ww&ixlib=rb-4.1.0&q=85)' }}
    />
    <div className="absolute inset-0 bg-[#1B3D6E]/85" />
    <div className="relative z-10 max-w-6xl mx-auto px-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
        {stats.map((stat) => (
          <div key={stat.id} className="space-y-2">
            <AnimatedCounter target={stat.value} suffix={stat.suffix} />
            <p className="text-[#F5C518] text-sm tracking-wider uppercase font-medium">
              {stat.label}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ============= TESTIMONIALS SECTION =============
export const TestimonialsSection = () => {
  const [active, setActive] = useState(0);
  const intervalRef = useRef(null);

  const startAutoplay = () => {
    intervalRef.current = setInterval(() => {
      setActive((prev) => (prev + 1) % testimonials.length);
    }, 5000);
  };

  useEffect(() => {
    startAutoplay();
    return () => clearInterval(intervalRef.current);
  }, []);

  const goTo = (idx) => {
    clearInterval(intervalRef.current);
    setActive(idx);
    startAutoplay();
  };

  const t = testimonials[active];

  return (
    <section id="testimonials" className="py-20 lg:py-28 bg-[#EEF6FF]">
      <div className="max-w-4xl mx-auto px-4">
        <SectionTitle title="Guest's Experience" subtitle="What our guests say" />
        <div className="relative bg-white rounded-2xl p-8 md:p-12 shadow-lg border border-gray-50 mt-8">
          <Quote size={48} className="text-[#F5C518]/20 absolute top-6 left-6" />
          <div className="text-center" key={active} style={{ animation: 'fadeIn 0.5s ease-out' }}>
            {/* Stars */}
            <div className="flex justify-center gap-1 mb-6">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  size={18}
                  className={i < t.rating ? 'text-[#F5C518] fill-[#F5C518]' : 'text-gray-200'}
                />
              ))}
            </div>
            <p className="text-lg md:text-xl text-[#2D2D2D] leading-relaxed italic mb-8 font-light">
              "{t.text}"
            </p>
            <div className="w-14 h-14 mx-auto rounded-full bg-[#1B3D6E] flex items-center justify-center text-[#F5C518] font-bold text-lg mb-3">
              {t.avatar}
            </div>
            <h4 className="font-['Playfair_Display'] font-semibold text-[#1B3D6E] text-lg">{t.name}</h4>
            <p className="text-[#6B6B6B] text-sm">{t.location}</p>
          </div>

          {/* Navigation */}
          <div className="flex justify-center items-center gap-4 mt-8">
            <button
              onClick={() => goTo((active - 1 + testimonials.length) % testimonials.length)}
              className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-[#6B6B6B] hover:border-[#F5C518] hover:text-[#D4A010] transition-all duration-300"
            >
              <ChevronLeft size={16} />
            </button>
            <div className="flex gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`rounded-full transition-all duration-300 ${
                    i === active ? 'w-6 h-2 bg-[#F5C518]' : 'w-2 h-2 bg-gray-300 hover:bg-[#F5C518]/50'
                  }`}
                />
              ))}
            </div>
            <button
              onClick={() => goTo((active + 1) % testimonials.length)}
              className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-[#6B6B6B] hover:border-[#F5C518] hover:text-[#D4A010] transition-all duration-300"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============= CONTACT SECTION =============

export const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const WHATSAPP_NUMBER = contactInfo.whatsapp;

  const handleSubmit = (e) => {
    e.preventDefault();

    const text = `Hello Dreamland Inn 🏝️

Name: ${formData.name}
Phone: ${formData.phone}
Email: ${formData.email}

Message:
${formData.message}`;

    const whatsappURL = `https://wa.me/91${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;

    window.open(whatsappURL, "_blank");

    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  return (
    <section id="contact" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">

        {/* Title */}
        <h2 className="text-3xl font-bold text-center text-[#1B3D6E]">
          Contact Us
        </h2>
        <p className="text-center text-gray-500 mt-2">
          We'd love to hear from you
        </p>

        <div className="grid lg:grid-cols-2 gap-12 mt-10">

          {/* Map */}
          <div className="rounded-xl overflow-hidden shadow-lg h-[400px] lg:h-auto">
            <iframe
              src={contactInfo.mapUrl}
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: '400px' }}
              loading="lazy"
              title="Dreamland Inn Location"
            />
          </div>

          {/* Form */}
          <div>
            <form onSubmit={handleSubmit} className="space-y-5">

              {/* Name */}
              <input
                type="text"
                placeholder="Your Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2478C8] focus:ring-1 focus:ring-[#2478C8] transition-colors"
              />

              {/* Email + Phone */}
              <div className="grid md:grid-cols-2 gap-5">
                <input
                  type="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2478C8] focus:ring-1 focus:ring-[#2478C8] transition-colors"
                />
                <input
                  type="tel"
                  placeholder="Phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2478C8] focus:ring-1 focus:ring-[#2478C8] transition-colors"
                />
              </div>

              {/* Message */}
              <textarea
                rows={4}
                placeholder="Tell us about your visit plans..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2478C8] focus:ring-1 focus:ring-[#2478C8] transition-colors"
              />

              {/* Submit */}
              <button
                type="submit"
                className="w-full py-3.5 bg-[#F5C518] text-[#1B3D6E] font-semibold rounded-lg hover:bg-[#D4A010] transition-colors duration-300"
              >
                Send via WhatsApp
              </button>

            </form>

            {/* Contact Info */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">

              {/* Main Phone */}
              <div className="p-4 bg-[#EEF6FF] rounded-lg">
                <p className="text-xs mb-1 text-[#2478C8] font-medium uppercase tracking-wide">Main Phone</p>
                <a href={`tel:${contactInfo.phone}`} className="font-medium text-sm text-[#1B3D6E]">
                  {contactInfo.phone}
                </a>
              </div>

              {/* WhatsApp */}
              <div className="p-4 bg-[#EEF6FF] rounded-lg">
                <p className="text-xs mb-1 text-[#2478C8] font-medium uppercase tracking-wide">WhatsApp</p>
                <a
                  href={`https://wa.me/91${contactInfo.whatsapp}`}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-sm text-green-600"
                >
                  Chat Now
                </a>
              </div>

              {/* Reception Numbers */}
              <div className="p-4 bg-[#EEF6FF] rounded-lg sm:col-span-2">
                <p className="text-xs mb-2 text-[#2478C8] font-medium uppercase tracking-wide">Reception</p>

                {contactInfo.reception.map((num, index) => (
                  <div key={index} className="flex justify-between items-center mb-2">
                    <a href={`tel:+91${num}`} className="text-sm font-medium text-[#1B3D6E]">
                      +91 {num}
                    </a>

                    {/* <a
                      href={`https://wa.me/91${num}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-green-600 text-xs"
                    >
                      WhatsApp
                    </a> */}
                  </div>
                ))}
              </div>

            </div>

          </div>

        </div>
      </div>
    </section>
  );
};